import Svg, {
  Path,
  type PathProps,
  Polygon,
  type SvgProps,
} from 'react-native-svg'

import {usePalette} from '#/lib/hooks/usePalette'

/**
 * Official Eurosky wordmark - the word "Eurosky" with the 12-point star as
 * a trailing superscript (the "Eurosky+" lockup). Shapes are verbatim from
 * `branding/eurosky_logos/wordmark/eurosky-logo-black.svg` (E and k are
 * polygons, the rest are paths, last path is the star).
 *
 * This is the COMPLETE brand lockup - it already contains the star, so it
 * is rendered on its own. Upstream's adjacent <Logo> (the standalone star)
 * has been removed at the few sites that paired them; <Logo> is still used
 * by itself elsewhere as the icon-only mark.
 */
const VIEWBOX_WIDTH = 1551.3365
const VIEWBOX_HEIGHT = 514.0007
const ratio = VIEWBOX_HEIGHT / VIEWBOX_WIDTH

export function Logotype({
  fill,
  ...rest
}: {fill?: PathProps['fill']} & SvgProps) {
  const pal = usePalette('default')
  // @ts-ignore it's fiiiiine
  const size = parseInt(rest.width || 32)
  const _fill = fill || pal.text.color

  return (
    <Svg
      fill="none"
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
      {...rest}
      width={size}
      height={Number(size) * ratio}>
      {/* E */}
      <Polygon
        fill={_fill}
        points="57.2705 315.8084 173.9603 315.8084 173.9603 275.1243 57.2705 275.1243 57.2705 202.3892 182.9866 202.3892 182.9866 161.7051 8.7388 161.7051 8.7388 429.6179 184.0325 429.6179 184.0325 388.9337 57.2705 388.9337 57.2705 315.8084"
      />
      {/* u */}
      <Path
        fill={_fill}
        d="M340.1185,345.1116c0,10.2023-2.1154,18.6846-6.3462,25.4419-4.2308,6.7599-9.6141,11.8389-16.1555,15.2396-6.5414,3.4034-13.2986,5.1025-20.277,5.1025-11.3368,0-20.493-3.7468-27.4714-11.2509-6.9758-7.4988-10.4651-17.6152-10.4651-30.3492v-120.611h-47.3556v127.9381c0,16.0463,2.8335,29.7169,8.5032,41.0094,5.6697,11.295,13.5406,19.8841,23.6127,25.7723,10.0723,5.8856,21.6926,8.8311,34.8636,8.8311,15.0861,0,27.7992-3.5751,38.1317-10.7279,10.335-7.1501,17.7687-16.3533,22.304-27.6015h2.0945v35.7118h45.9167v-200.9333h-47.3556v116.4271Z"
      />
      {/* r */}
      <Path
        fill={_fill}
        d="M514.1997,225.8068c-11.5111,0-21.7368,3.1614-30.6746,9.4841-8.9402,6.3228-15.2422,15.2839-18.9031,26.8808h-2.0947v-33.4872h-45.9166v200.9333h47.3556v-118.1261c0-8.5474,1.9645-16.0905,5.8882-22.6318,3.9238-6.5413,9.3073-11.6646,16.1555-15.3724,6.8458-3.7051,14.5867-5.5577,23.22-5.5577,4.0095,0,8.1753.2836,12.4919.8482,4.3167.5698,7.478,1.2021,9.4841,1.8969v-43.562c-2.1804-.4345-4.9048-.7623-8.1754-.9809-3.2706-.2159-6.2135-.3253-8.8309-.3253Z"
      />
      {/* o */}
      <Path
        fill={_fill}
        d="M683.8159,239.0195c-14.3889-8.6359-31.3953-12.9525-51.0193-12.9525s-36.6277,4.3167-51.0164,12.9525c-14.3916,8.6333-25.5332,20.7558-33.4248,36.3649-7.8943,15.6117-11.8389,33.7968-11.8389,54.5526s3.9446,38.8966,11.8389,54.4173c7.8916,15.5258,19.0332,27.6041,33.4248,36.2374,14.3887,8.6332,31.395,12.9499,51.0164,12.9499s36.6304-4.3167,51.0193-12.9499c14.3914-8.6333,25.5303-20.7116,33.4246-36.2374,7.8918-15.5207,11.8389-33.6615,11.8389-54.4173s-3.947-38.9409-11.8389-54.5526c-7.8943-15.6091-19.0332-27.7316-33.4246-36.3649ZM675.7705,363.0312c-3.5308,9.9421-8.8752,17.8572-16.0254,23.7455-7.1501,5.8856-16.0461,8.8284-26.6855,8.8284-10.9023,0-19.9492-2.9428-27.1436-8.8284-7.197-5.8882-12.5598-13.8034-16.0906-23.7455-3.5334-9.942-5.2976-21.0159-5.2976-33.2269,0-12.2968,1.7642-23.4358,5.2976-33.4221,3.5308-9.9863,8.8936-17.9431,16.0906-23.8755,7.1943-5.9299,16.2412-8.8961,27.1436-8.8961,10.6394,0,19.5354,2.9662,26.6855,8.8961,7.1501,5.9324,12.4946,13.8892,16.0254,23.8755,3.5334,9.9863,5.2976,21.1253,5.2976,33.4221,0,12.211-1.7642,23.2849-5.2976,33.2269Z"
      />
      {/* s */}
      <Path
        fill={_fill}
        d="M859.1433,315.0226l-34.2756-7.3245c-10.2021-2.3547-17.5085-5.3652-21.9111-9.0287-4.405-3.6609-6.5645-8.4147-6.4761-14.2587-.0884-6.8015,3.2056-12.3385,9.877-16.6135,6.6714-4.2724,14.9326-6.4086,24.7888-6.4086,7.3271,0,13.5171,1.1761,18.5779,3.5308,5.0557,2.3548,9.0913,5.4511,12.0991,9.289,3.0105,3.8379,5.1233,7.936,6.3462,12.2968l43.1689-4.7095c-3.229-17.0922-11.6228-30.6536-25.1816-40.6841-13.564-10.0279-32.116-15.0445-55.6636-15.0445-16.0464,0-30.1982,2.5083-42.4509,7.5223-12.2527,5.0165-21.8018,12.0809-28.6475,21.1929-6.8484,9.1147-10.2258,19.8633-10.1399,32.246-.0859,14.6516,4.491,26.7507,13.7358,36.3025,9.2446,9.5491,23.5034,16.3298,42.7788,20.342l34.2729,7.1944c9.2446,2.0061,16.0696,4.8839,20.4722,8.6333,4.405,3.7494,6.6062,8.5032,6.6062,14.2587,0,6.8015-3.4216,12.5154-10.2673,17.1365-6.8481,4.6237-15.8953,6.9342-27.146,6.9342-10.9023,0-19.7515-2.3105-26.5557-6.9342-6.8015-4.6211-11.2483-11.4669-13.3428-20.5372l-46.177,4.4467c2.8777,18.4895,11.8157,32.9017,26.8157,43.2367,15.0005,10.3323,34.7986,15.4998,59.3923,15.4998,16.7437,0,31.5696-2.7034,44.4778-8.1103,12.9058-5.4069,23.0012-12.9291,30.2815-22.5641,7.283-9.6376,10.9673-20.7792,11.0559-33.4247-.0886-14.3888-4.7747-26.0325-14.0637-34.9286-9.2864-8.8961-23.4385-15.3906-42.4482-19.4912Z"
      />
      {/* k */}
      <Polygon
        fill={_fill}
        points="1120.0464 228.6846 1064.7107 228.6846 992.4985 309.3972 989.228 309.3972 989.228 161.7051 941.8726 161.7051 941.8726 429.6179 989.228 429.6179 989.228 362.0796 1006.458 343.6086 1067.9814 429.6179 1124.6233 429.6179 1042.1465 315.4175 1120.0464 228.6846"
      />
      {/* y */}
      <Path
        fill={_fill}
        d="M1228.8367,380.4305h-2.092l-46.3096-151.746h-50.2332l72.865,205.1199-4.0564,10.8579c-2.8777,7.5873-6.3904,13.015-10.53,16.2856-4.145,3.2707-8.9194,4.9489-14.3264,5.0374-5.407.0859-11.5554-.8743-18.4426-2.8778l-10.9907,36.8905c3.2266,1.3062,7.5664,2.4615,13.0176,3.4658,5.4485,1.0018,11.4019,1.504,17.8572,1.504,12.3801,0,23.043-2.027,31.9832-6.0834,8.938-4.0539,16.5044-9.8978,22.697-17.5294,6.1899-7.6315,11.1624-16.767,14.9119-27.4064l80.7151-225.004-50.6262-.2602-46.4397,151.746Z"
      />
      {/* + (star superscript) */}
      <Path
        fill={_fill}
        d="M1540.0918,136.3978c-124.0293-.0055-124.8535-.8296-124.8591-124.8591v-2.5059h-12.063v2.5059c-.0056,124.0295-.8298,124.8536-124.8591,124.8591-.8298,0-1.665,0-2.5059,0v12.063c.8408,0,1.676,0,2.5059,0,124.0293.0055,124.8535.8298,124.8591,124.8591v2.5059h12.063v-2.5059c.0056-124.0294.8298-124.8536,124.8591-124.8591.8298,0,1.665,0,2.5059,0v-12.063c-.8408,0-1.676,0-2.5059,0Z"
      />
    </Svg>
  )
}
